import { useCallback, useSyncExternalStore } from 'react'
import { EDUCATOR_HOMEPAGE_KEY } from '@educator/api-hooks/homepage'
import { subjectDetailQueryKey } from '@educator/api-hooks/learning/useSubjectDetail'
import {
  notifyManager,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import type { HomepageResponse, SubjectDetailResponse } from '@mc/educator-api'

const cachedThemeKey = (queryClient: QueryClient, slug: string) =>
  queryClient.getQueryData<SubjectDetailResponse>(subjectDetailQueryKey(slug))
    ?.pathSlug ||
  queryClient
    .getQueryData<HomepageResponse>(EDUCATOR_HOMEPAGE_KEY)
    ?.subjectList?.subjects?.find((subject) => subject.slug === slug)
    ?.pathSlug ||
  slug

/** Reads the query cache without creating entries: an observer here, even a
 *  disabled one, would create empty queries that make later `initialData`
 *  (SSR-seeded pages) be ignored. */
export function useSubjectThemeKey(slug: string): string
export function useSubjectThemeKey(slug: string | undefined): string | undefined
export function useSubjectThemeKey(slug: string | undefined) {
  const queryClient = useQueryClient()
  const subscribe = useCallback(
    (onChange: () => void) =>
      queryClient.getQueryCache().subscribe(notifyManager.batchCalls(onChange)),
    [queryClient],
  )
  const getSnapshot = () => (slug ? cachedThemeKey(queryClient, slug) : slug)

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
